import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import BookmarkFilledIcon from "@/assets/svgs/bookmark-filled.svg";
import BookmarkOutlineIcon from "@/assets/svgs/bookmark-outline.svg";
import CheckSmallIcon from "@/assets/svgs/check-small.svg";
import ChevronDownIcon from "@/assets/svgs/chevron-down.svg";
import HeartIcon from "@/assets/svgs/new-wishlist.svg";
import WishlistSnackbarIcon from "@/assets/svgs/wishlist-heart-filled.svg";
import { Snackbar } from "@/components/shared/data-display/snackbar/snackbar";
import { Button } from "@/components/shared/inputs/button/button";
import { DropdownMenu } from "@/components/shared/inputs/dropdown-menu/dropdown-menu";
import { Input } from "@/components/shared/inputs/input/input";
import { Link } from "@/components/shared/inputs/link/link";
import useIsClient from "@/hooks/useIsClient";
import {
  addToList,
  createUserList,
  getListsForEdition,
  removeFromList,
} from "@/services/list.service";
import {
  createOrUpdateWorkEngagementStatus,
  removeWorkEngagementStatus,
} from "@/services/user-work-engagement.service";
import type { UserList } from "@/types/list.types";
import { UserWorkEngagementStatus } from "@/types/user-work-engagement.types";
import { legacyLoginLink, legacyWishlistLoginLink } from "@/utility/url";
import styles from "./work-list-actions.module.scss";
import Gtm, {
  type TagManagerItemModel,
} from "@/infrastructure/observability/google-tag-manager.service";
import {
  trackAddedToList,
  trackRemovedFromList,
} from "@/utility/analytics.utility";

interface WorkListActionsProps {
  idAmazon: number;
  idWork: number;
  initialSelectedListId?: number;
  initialEngagementStatus: UserWorkEngagementStatus;
  isLoggedIn: boolean;
  tagManagerItem?: TagManagerItemModel;
}

interface SnackbarState {
  message: ReactNode;
  type: "critical" | "success";
  undo?: () => void;
}

interface ListRequest {
  idAmazon: number;
  promise: Promise<UserList[]>;
}

const WorkListActions = ({
  idAmazon,
  idWork,
  initialSelectedListId = 0,
  initialEngagementStatus,
  isLoggedIn,
  tagManagerItem,
}: WorkListActionsProps) => {
  const isClient = useIsClient();
  const [lists, setLists] = useState<UserList[]>([]);
  const [loadedListsIdAmazon, setLoadedListsIdAmazon] = useState<number | null>(
    null,
  );
  const [isLoadingLists, setIsLoadingLists] = useState(isLoggedIn);
  const [isAddingToWishList, setIsAddingToWishList] = useState(false);
  const [selectedListId, setSelectedListId] = useState(initialSelectedListId);
  const [engagementStatus, setEngagementStatus] = useState(
    initialEngagementStatus,
  );
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateListVisible, setIsCreateListVisible] = useState(false);
  const [isSavingNewList, setIsSavingNewList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListError, setNewListError] = useState("");
  const listRequestRef = useRef<ListRequest | null>(null);

  const requestLists = useCallback(() => {
    if (!isLoggedIn) return Promise.resolve([]);
    if (listRequestRef.current?.idAmazon === idAmazon) {
      return listRequestRef.current.promise;
    }

    const promise = getListsForEdition(idWork, idAmazon)
      .then((response) => response.userLists)
      .catch((error: unknown) => {
        if (listRequestRef.current?.idAmazon === idAmazon) {
          listRequestRef.current = null;
        }
        throw error;
      });
    listRequestRef.current = { idAmazon, promise };
    return promise;
  }, [idAmazon, idWork, isLoggedIn]);

  const applyLoadedLists = useCallback(
    (nextLists: UserList[]) => {
      setLists(nextLists);
      setLoadedListsIdAmazon(idAmazon);
      setSelectedListId((current) =>
        nextLists.some(
          (list) => list.idList === current && Boolean(list.idListItem),
        )
          ? current
          : (nextLists.find((list) => Boolean(list.idListItem))?.idList ?? 0),
      );
      setIsLoadingLists(false);
      return nextLists;
    },
    [idAmazon],
  );

  const loadLists = useCallback(async () => {
    if (!isLoggedIn) return [];
    setIsLoadingLists(true);
    try {
      return applyLoadedLists(await requestLists());
    } catch {
      setIsLoadingLists(false);
      return [];
    }
  }, [applyLoadedLists, isLoggedIn, requestLists]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let isCurrentSelection = true;

    requestLists()
      .then((nextLists) => {
        if (isCurrentSelection) applyLoadedLists(nextLists);
      })
      .catch(() => {
        if (isCurrentSelection) setIsLoadingLists(false);
      });

    return () => {
      isCurrentSelection = false;
    };
  }, [applyLoadedLists, isLoggedIn, requestLists]);

  const showSnackbar = (
    message: ReactNode,
    type: SnackbarState["type"],
    undo?: () => void,
  ) => setSnackbar({ message, type, undo });

  const replaceListMembership = (idList: number, idListItem: number | null) => {
    setLists((current) =>
      current.map((list) =>
        list.idList === idList ? { ...list, idListItem } : list,
      ),
    );
  };

  /**
   * Adds the active edition to a list.
   * @param list Destination list.
   * @param allowUndo Whether to offer an undo action.
   * @param useDefaultList Whether the server should resolve the default list.
   * @returns True when the edition was added.
   */
  async function addEditionToList(
    list: UserList,
    allowUndo = true,
    useDefaultList = false,
  ) {
    const response = await addToList(
      useDefaultList ? null : list.idList,
      idAmazon,
    );

    if (!response.success) {
      showSnackbar(
        "We couldn't update that list. Please try again.",
        "critical",
      );
      return false;
    }

    if (tagManagerItem) {
      Gtm.sendWorkAddToWishlistEvent(tagManagerItem);
    }

    trackAddedToList(list.title, response.idList, idAmazon, idWork);
    replaceListMembership(list.idList, response.idListItem);
    setSelectedListId(response.idList);
    showSnackbar(
      <>
        Added to{" "}
        <Link
          href={`/list/view/${list.idList}/`}
          linkType="legacy"
          className={styles.snackbarLink}
        >
          {list.title}
        </Link>
        !
      </>,
      "success",
      allowUndo
        ? () => {
            void removeEditionFromList(
              { ...list, idListItem: response.idListItem },
              false,
            );
          }
        : undefined,
    );
    return true;
  }

  /**
   * Removes the active edition from a list.
   * @param list List containing the edition.
   * @param allowUndo Whether to offer an undo action.
   * @returns True when the edition was removed.
   */
  async function removeEditionFromList(list: UserList, allowUndo = true) {
    if (!list.idListItem) return false;
    const response = await removeFromList(list.idListItem);
    if (!response.success) {
      showSnackbar(
        "We couldn't update that list. Please try again.",
        "critical",
      );
      return false;
    }
    trackRemovedFromList(list.title, list.idList, idAmazon, idWork);
    const remainingSelectedList = lists.find(
      (candidate) =>
        candidate.idList !== list.idList && Boolean(candidate.idListItem),
    );
    replaceListMembership(list.idList, null);
    setSelectedListId(remainingSelectedList?.idList ?? 0);
    showSnackbar(
      <>
        Removed from{" "}
        <Link
          href={`/list/view/${list.idList}/`}
          linkType="legacy"
          className={styles.snackbarLink}
        >
          {list.title}
        </Link>
        !
      </>,
      "success",
      allowUndo
        ? () => {
            void addEditionToList(list, false);
          }
        : undefined,
    );
    return true;
  }

  const handleAddToWishList = async () => {
    if (!isLoggedIn) {
      window.location.assign(
        legacyWishlistLoginLink(idAmazon, window.location.href),
      );
      return;
    }
    setIsAddingToWishList(true);
    const availableLists =
      loadedListsIdAmazon === idAmazon && lists.length
        ? lists
        : await loadLists();
    const currentDefault = availableLists.find((list) => list.isDefault);
    if (currentDefault && !currentDefault.idListItem) {
      await addEditionToList(currentDefault, true, true);
    }
    setIsAddingToWishList(false);
  };

  const handleListToggle = async (list: UserList, closeMenu: () => void) => {
    closeMenu();

    if (list.idListItem) {
      await removeEditionFromList(list);
    } else {
      await addEditionToList(list);
    }
  };

  const updateEngagementStatus = async (
    nextStatus: UserWorkEngagementStatus,
    previousStatus: UserWorkEngagementStatus,
    allowUndo: boolean,
  ) => {
    setEngagementStatus(nextStatus);
    const success =
      nextStatus === UserWorkEngagementStatus.None
        ? await removeWorkEngagementStatus(idWork)
        : await createOrUpdateWorkEngagementStatus(idWork, nextStatus);
    if (!success) {
      setEngagementStatus(previousStatus);
      showSnackbar(
        "We couldn't update your reading list. Please try again.",
        "critical",
      );
      return;
    }

    const changedStatus =
      nextStatus === UserWorkEngagementStatus.None
        ? previousStatus
        : nextStatus;
    const listName =
      changedStatus === UserWorkEngagementStatus.WantToRead
        ? "Want to Read"
        : "Already Read It";
    const listUrl =
      changedStatus === UserWorkEngagementStatus.WantToRead
        ? "/list/want-to-read/"
        : "/list/already-read/";
    showSnackbar(
      <>
        {nextStatus === UserWorkEngagementStatus.None
          ? "Removed from"
          : "Added to"}{" "}
        <Link href={listUrl} linkType="legacy" className={styles.snackbarLink}>
          {listName}
        </Link>
        !
      </>,
      "success",
      allowUndo
        ? () => {
            void updateEngagementStatus(previousStatus, nextStatus, false);
          }
        : undefined,
    );
  };

  const handleEngagementToggle = (
    selectedStatus: UserWorkEngagementStatus,
    closeMenu: () => void,
  ) => {
    closeMenu();
    const nextStatus =
      engagementStatus === selectedStatus
        ? UserWorkEngagementStatus.None
        : selectedStatus;
    void updateEngagementStatus(nextStatus, engagementStatus, true);
  };

  const handleCreateList = async (closeMenu: () => void) => {
    const title = newListName.trim();
    if (!title || isSavingNewList) return;
    if (title.length > 50) {
      setNewListError(
        "Your list name is too long. Use 50 characters or fewer.",
      );
      return;
    }
    setIsSavingNewList(true);
    setNewListError("");
    const createdList = await createUserList(title, false);
    if (!createdList) {
      setNewListError("Cannot create a new list. Please try again.");
      setIsSavingNewList(false);
      return;
    }
    const createdWithMembership = { ...createdList, idListItem: null };
    setLists((current) => [...current, createdWithMembership]);
    if (await addEditionToList(createdWithMembership)) {
      setIsCreateListVisible(false);
      setNewListName("");
      setNewListError("");
      closeMenu();
    }
    setIsSavingNewList(false);
  };

  const loginUrl = isClient ? legacyLoginLink(window.location.href) : "#";
  const wishListLoginUrl = isClient
    ? legacyWishlistLoginLink(idAmazon, window.location.href)
    : "#";
  const currentLists = loadedListsIdAmazon === idAmazon ? lists : [];
  const isSavedToAnyList =
    loadedListsIdAmazon === idAmazon
      ? currentLists.some((list) => Boolean(list.idListItem))
      : initialSelectedListId > 0;
  const effectiveSelectedListId =
    loadedListsIdAmazon === idAmazon ? selectedListId : initialSelectedListId;
  const selectedList =
    currentLists.find(
      (list) =>
        list.idList === effectiveSelectedListId && Boolean(list.idListItem),
    ) ?? currentLists.find((list) => Boolean(list.idListItem));
  const selectedListLinkId = selectedList?.idList ?? effectiveSelectedListId;
  const SaveBookmarkIcon = isSavedToAnyList
    ? BookmarkFilledIcon
    : BookmarkOutlineIcon;
  const saveButtonContent = (
    <>
      <SaveBookmarkIcon className={styles.bookmarkIcon} aria-hidden="true" />
      <span className={styles.saveToListText}>
        {isSavedToAnyList ? "Saved to List" : "Save to List"}
      </span>
      <ChevronDownIcon
        className={styles.chevronIcon}
        aria-hidden="true"
        data-testid="save-to-list-chevron"
      />
    </>
  );
  const saveButtonClassName = classNames(styles.saveToListTrigger, {
    [styles.menuOpen]: isMenuOpen,
  });
  const saveButton = isLoggedIn ? (
    <button
      type="button"
      className={saveButtonClassName}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
    >
      {saveButtonContent}
    </button>
  ) : (
    <Link href={loginUrl} linkType="legacy" className={saveButtonClassName}>
      {saveButtonContent}
    </Link>
  );

  const renderListOption = (list: UserList, closeMenu: () => void) => (
    <label key={list.idList} className={styles.menuOption}>
      <input
        type="checkbox"
        checked={Boolean(list.idListItem)}
        onChange={() => {
          void handleListToggle(list, closeMenu);
        }}
      />
      <span>{list.title}</span>
      {list.isDefault && <small>(default)</small>}
      {Boolean(list.idListItem) && (
        <CheckSmallIcon
          className={styles.selectedCheckIcon}
          aria-hidden="true"
        />
      )}
    </label>
  );

  return (
    <div className={styles.root}>
      {isSavedToAnyList ? (
        <div className={styles.addedWishList}>
          <HeartIcon className={styles.heartIcon} aria-hidden="true" />
          <span className={styles.singleLineMessage}>
            Added to{" "}
            <Link
              href={`/list/view/${selectedListLinkId}/`}
              linkType="legacy"
              underline="hover"
            >
              Wish List
            </Link>
          </span>
        </div>
      ) : (
        <Button
          text="Add to Wish List"
          variant="link"
          icon={HeartIcon}
          iconClassName={styles.heartIcon}
          emphasis
          loading={isAddingToWishList}
          href={isLoggedIn ? undefined : wishListLoginUrl}
          onClick={handleAddToWishList}
          className={styles.wishListButton}
        />
      )}

      <div className={styles.actionDivider} />

      {isLoggedIn ? (
        <DropdownMenu
          className={styles.listDropdown}
          triggerComponent={saveButton}
          placement="bottom-start"
          width="270px"
          leaveOpenUntilClickAway
          onMenuToggle={(open) => {
            setIsMenuOpen(open);
            if (open) void loadLists();
            else {
              setIsCreateListVisible(false);
              setNewListName("");
              setNewListError("");
            }
          }}
          renderContent={(closeMenu) => (
            <li className={styles.menuContent} role="none">
              <strong className={styles.menuHeading}>My Lists</strong>
              {isLoadingLists ? (
                <p className={styles.loading}>Loading lists…</p>
              ) : (
                <>
                  <div className={styles.listGroup}>
                    {lists
                      .filter((list) => list.isDefault)
                      .map((list) => renderListOption(list, closeMenu))}
                  </div>
                  <div className={styles.divider} />
                  <div
                    className={styles.statusGroup}
                    role="radiogroup"
                    aria-label="Reading status"
                  >
                    {[
                      [UserWorkEngagementStatus.WantToRead, "Want to Read"],
                      [UserWorkEngagementStatus.AlreadyRead, "Already Read It"],
                    ].map(([value, label]) => (
                      <label key={value} className={styles.menuOption}>
                        <input
                          type="radio"
                          name={`reading-status-${idWork}`}
                          checked={engagementStatus === value}
                          onClick={() =>
                            handleEngagementToggle(
                              value as UserWorkEngagementStatus,
                              closeMenu,
                            )
                          }
                          onChange={() => {}}
                        />
                        <span>{label}</span>
                        {engagementStatus === value && (
                          <CheckSmallIcon
                            className={styles.selectedCheckIcon}
                            aria-hidden="true"
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  {lists.some((list) => !list.isDefault) && (
                    <>
                      <div className={styles.divider} />
                      <div className={styles.listGroup}>
                        {lists
                          .filter((list) => !list.isDefault)
                          .map((list) => renderListOption(list, closeMenu))}
                      </div>
                    </>
                  )}
                  <div className={styles.divider} />
                  <Button
                    text="+ Create New List"
                    variant="link"
                    emphasis
                    onClick={() => {
                      setIsCreateListVisible((current) => !current);
                      setNewListName("");
                      setNewListError("");
                    }}
                    className={styles.createListButton}
                  />
                  {isCreateListVisible && (
                    <form
                      className={styles.createListForm}
                      onSubmit={(event) => {
                        event.preventDefault();
                        void handleCreateList(closeMenu);
                      }}
                    >
                      <Input
                        label="List Name"
                        value={newListName}
                        fullWidth
                        size="small"
                        maxLength={50}
                        disabled={isSavingNewList}
                        error={Boolean(newListError)}
                        helperText={newListError}
                        className={styles.createListInput}
                        onChange={(event) => {
                          setNewListName(event.target.value);
                          setNewListError("");
                        }}
                      />
                      <Button
                        text="Save"
                        type="submit"
                        size="small"
                        rounded
                        loading={isSavingNewList}
                        disabled={!newListName.trim() || isSavingNewList}
                        className={styles.createListSaveButton}
                      />
                    </form>
                  )}
                </>
              )}
            </li>
          )}
        />
      ) : (
        saveButton
      )}

      <Snackbar
        open={snackbar !== null}
        onOpenChange={(open) => {
          if (!open) setSnackbar(null);
        }}
        snackbarType={snackbar?.type ?? "success"}
        icon={snackbar?.type === "success" ? WishlistSnackbarIcon : undefined}
        autoHideDuration={5000}
        message={snackbar?.message ?? ""}
        actionText={snackbar?.undo ? "Undo" : undefined}
        onActionClick={() => {
          const undo = snackbar?.undo;
          setSnackbar(null);
          undo?.();
        }}
        className={classNames(styles.listActionSnackbar, {
          [styles.successSnackbar]: snackbar?.type === "success",
        })}
      />
    </div>
  );
};

export default WorkListActions;
